import { useState } from 'react'

import { api } from '../api'
import { formatDate } from '../lib/format'
import type { TicketDetail } from '../types'

/**
 * Согласование работ по заявке.
 *
 * Заявка оплачивается по факту, значит до начала работ надо договориться о
 * цене. Разговор идёт по кругу: специалист оценил — менеджер спросил клиента —
 * клиент назвал своё число — специалист согласился или объяснил, почему за
 * столько нельзя.
 *
 * Главное, что здесь видно, — на чьей стороне мяч. Без этого выходило так:
 * специалист поставил оценку в журнале, менеджер её не увидел, клиент ждёт
 * ответа, а через неделю выясняется, что все ждали друг друга.
 *
 * Кнопки показываем по очереди хода, а не по должности: сервер всё равно
 * проверит, но человек не должен гадать, что ему сейчас делать.
 */
export default function ApprovalBox({
  ticket,
  mayReply,
  onChanged,
  onError,
}: {
  ticket: TicketDetail
  /** Может ли этот человек говорить с клиентом — ходы менеджера */
  mayReply: boolean
  onChanged: () => void
  onError: (e: string) => void
}) {
  const [hours, setHours] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  // У обращений согласования нет: они покрыты тарифом, цену не обсуждают
  if (ticket.kind !== 'order') return null

  async function decide(decision: string, withHours = false) {
    setBusy(true)
    onError('')
    try {
      await api.approveTicket(ticket.id, {
        decision,
        hours: withHours ? Number(hours || ticket.estimate_hours || 0) : null,
        note: note.trim() || null,
      })
      setNote('')
      setHours('')
      onChanged()
    } catch (e: any) {
      onError(e?.message ?? 'Не удалось согласовать')
    } finally {
      setBusy(false)
    }
  }

  const ждёт = ticket.approval === 'estimated'
  const пересмотр = ticket.approval === 'rework'

  return (
    <div className={`appr appr-${ticket.approval}`}>
      <div className="appr-head">
        <b>Согласование:</b> {ticket.approval_title}
        {ticket.approval_at && (
          <span className="cell-sub">
            {formatDate(ticket.approval_at)}
            {ticket.approval_by ? ` · ${ticket.approval_by}` : ''}
          </span>
        )}
      </div>

      <div className="appr-numbers">
        <span>
          Оценка специалистов: <b>{ticket.estimate_hours ?? '—'}</b> ч
        </span>
        <span>
          Согласовано с клиентом: <b>{ticket.agreed_hours ?? '—'}</b> ч
        </span>
      </div>

      {ticket.approval_note && <p className="appr-note">{ticket.approval_note}</p>}

      {ticket.approval === 'none' && (
        <p className="hint" style={{ margin: 0 }}>
          Ждём оценку специалиста — он ставит её в журнале, в своей задаче.
        </p>
      )}

      {ждёт && mayReply && (
        <>
          <p className="hint" style={{ margin: '6px 0' }}>
            Обсудите с клиентом. Сошлись на оценке — согласуйте; назвал другое
            число — верните специалисту, пусть скажет, берётся ли.
          </p>
          <div className="grid-2">
            <div className="field">
              <label htmlFor="ap-h">Часов</label>
              <input
                id="ap-h"
                type="number"
                step="0.5"
                min="0"
                value={hours}
                placeholder={String(ticket.estimate_hours ?? '')}
                onChange={(e) => setHours(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="ap-n">Что сказал клиент</label>
              <input id="ap-n" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
          </div>
          <div className="appr-actions">
            <button className="btn primary" disabled={busy} onClick={() => void decide('agree', true)}>
              Клиент согласен
            </button>
            <button className="btn" disabled={busy} onClick={() => void decide('renegotiate', true)}>
              Клиент просит меньше
            </button>
            <button className="btn danger" disabled={busy} onClick={() => void decide('reject')}>
              Отказался
            </button>
          </div>
        </>
      )}

      {пересмотр && (
        <>
          <p className="hint" style={{ margin: '6px 0' }}>
            Клиент называет {ticket.agreed_hours} ч. Беретесь — примите, и эта
            цифра станет оценкой в журнале. Нет — напишите, почему, менеджеру
            идти с этим к клиенту.
          </p>
          <div className="field full">
            <label htmlFor="ap-r">Почему нельзя</label>
            <input
              id="ap-r"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="если не беретесь"
            />
          </div>
          <div className="appr-actions">
            <button className="btn primary" disabled={busy} onClick={() => void decide('accept')}>
              Берусь за {ticket.agreed_hours} ч
            </button>
            <button className="btn" disabled={busy} onClick={() => void decide('refuse')}>
              За столько нельзя
            </button>
          </div>
        </>
      )}

      {ticket.approval === 'agreed' && mayReply && (
        <div className="appr-actions">
          <button className="btn" disabled={busy} onClick={() => void decide('reject')}>
            Клиент передумал
          </button>
        </div>
      )}
    </div>
  )
}
